import type { Race, RaceLeg, RaceLegBook, RaceList, RowId, TopicState } from "@/lib/odds-screen/live/types";
import { usableWeight, type BookWeights } from "@/lib/weights-config";

const NO_LEGS: ReadonlyMap<RowId, number> = new Map();

function weightedLegFair(books: readonly RaceLegBook[], weights: BookWeights): number | null {
  let weightSum = 0;
  let fairSum = 0;
  for (const { book, fair } of books) {
    const weight = weights[book];
    if (!usableWeight(weight)) continue;
    weightSum += weight;
    fairSum += weight * fair;
  }
  return weightSum > 0 ? fairSum / weightSum : null;
}

export function raceWithWeights(race: Race, weights: BookWeights | null): Race {
  if (weights === null || race.legs.some((leg) => leg.books === null)) return race;
  const legs = race.legs.map((leg): RaceLeg => {
    if (leg.books === null || leg.fair === null || leg.odds === null) return leg;
    const fair = weightedLegFair(leg.books, weights);
    return fair === null ? leg : { ...leg, fair, ev: fair * leg.odds - 1, assumed: false };
  });
  if (legs.every((leg, index) => leg === race.legs[index])) return race;
  let hit: number | null = 1;
  for (const leg of legs) hit = hit === null || leg.fair === null ? null : hit * leg.fair;
  return { ...race, legs, hit, ev: hit === null ? null : race.boost * hit - 1, assumed: legs.filter((leg) => leg.assumed).length };
}

export function currentRace(state: TopicState<RaceList> | null, preferredId?: string | null): Race | null {
  const races = state?.value?.races;
  if (state === null || state.phase === "ended" || races === undefined || races.size === 0) return null;
  const preferred = preferredId == null ? undefined : races.get(preferredId);
  if (preferred !== undefined) return preferred;
  let newest: Race | null = null;
  for (const race of races.values()) {
    if (newest === null || race.sinceMs > newest.sinceMs) newest = race;
  }
  return newest;
}

export function raceLegBoosts(race: Race | null): ReadonlyMap<RowId, number> {
  if (race === null) return NO_LEGS;
  const out = new Map<RowId, number>();
  for (const leg of race.legs) {
    if (leg.row !== null) out.set(leg.row, race.boost);
  }
  return out;
}

export const RACE_APP_LINK = "betrpickem://picks/home";
export const RACE_APP_STORE_PAGE = "https://l.betr.app/";
export const RACE_WEB_LINK = "https://picks.betr.app/";
export const RACE_APP_WAIT_MS = 2500;

const PHONE_OR_TABLET = /iPhone|iPad|iPod|Android/i;

export function racePlaceLink(userAgent: string): { readonly href: string; readonly app: boolean } {
  return PHONE_OR_TABLET.test(userAgent) ? { href: RACE_APP_LINK, app: true } : { href: RACE_WEB_LINK, app: false };
}

export const RACE_ASSUMED_NOTE = "No usable market price to compare against. Counted as 50/50.";

function legWord(count: number): string {
  return count === 1 ? "leg" : "legs";
}

export function raceAssumedText(assumed: number): string {
  return `incl. ${assumed} est. ${legWord(assumed)}`;
}

export function raceAssumedLabel(assumed: number): string {
  return `includes ${assumed} estimated ${legWord(assumed)}`;
}

export function racePctText(share: number | null): string {
  return share === null ? "–" : `${(share * 100).toFixed(1)}%`;
}

export function raceEvText(ev: number | null): string {
  if (ev === null) return "–";
  const pct = (Math.abs(ev) * 100).toFixed(1);
  return ev < 0 ? `−${pct}%` : `+${pct}%`;
}

export function raceSinceText(sinceMs: number, nowMs: number): string {
  const elapsed = Math.max(0, nowMs - sinceMs);
  if (elapsed < 60_000) return "just now";
  if (elapsed < 3_600_000) return `${Math.floor(elapsed / 60_000)}m ago`;
  return `${Math.floor(elapsed / 3_600_000)}h ago`;
}

export function raceMatchup(leg: RaceLeg): string {
  return [leg.team, leg.opp].filter((part) => part !== "").join(" ");
}

export function raceLegLine(leg: RaceLeg): string {
  const side = leg.side === "under" ? "Under" : "Over";
  return `${side} ${leg.line} · ${leg.market}`;
}
